/**
 * Where the analytics layer gets user values that call sites don't pass.
 *
 * Redux is in-memory only, so a full reload (refresh, DigiLocker / OneMoney /
 * eSign vendor return) wipes `state.user`. Lookup order for each value:
 *   1. a value remembered via `rememberUser()` (in memory; optionally
 *      sessionStorage when REACT_APP_MOE_PERSIST_CONTEXT=true)
 *   2. the redux store (`state.user`), when registered via initAnalytics({ store })
 *   3. an existing sessionStorage fallback (email only: services/journey getVerifiedEmail)
 *
 * Read-only towards the app: never dispatches, never writes app-owned keys.
 */
import { config } from './config';
import { getLeadId, getVerifiedEmail } from '../services/journey';

const STORAGE_KEY = 'ekyc_moe_ctx';
const KEYS = ['mobile', 'email', 'fullName', 'ekycUserId', 'clientName', 'clientId', 'dob'];

/** @type {{ getState: () => any } | null} */
let store = null;
/** @type {Record<string, string>} */
let remembered = {};

const loadPersisted = () => {
    if (!config.persistContext) return;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (raw) remembered = { ...JSON.parse(raw), ...remembered };
    } catch (_) { /* storage blocked — memory only */ }
};

const persist = () => {
    if (!config.persistContext) return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(remembered));
    } catch (_) { /* storage blocked — memory only */ }
};

loadPersisted();

/** Give the layer read access to the redux store. */
export const registerAnalyticsStore = (reduxStore) => {
    store = reduxStore && typeof reduxStore.getState === 'function' ? reduxStore : null;
};

/** Safe read of `state.user` (or {}). */
export const readUserState = () => {
    try {
        const state = store ? store.getState() : null;
        return (state && state.user) || {};
    } catch (_) {
        return {};
    }
};

const clean = (v) => (v == null ? '' : String(v).trim());

/**
 * Remember user values for later events. Empty values are ignored, so a
 * partial update never erases what is already known.
 * @param {{ mobile?: string, email?: string, clientName?: string, clientId?: string, dob?: string }} values
 */
export const rememberUser = (values = {}) => {
    let changed = false;
    KEYS.forEach((key) => {
        const v = clean(values[key]);
        if (v && remembered[key] !== v) {
            remembered[key] = v;
            changed = true;
        }
    });
    if (changed) persist();
};

export const forgetUser = () => {
    remembered = {};
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* ignore */ }
};

const safeVerifiedEmail = () => {
    try {
        return getVerifiedEmail();
    } catch (_) {
        return null;
    }
};

// ---------------------------------------------------------------------------
// eSign attempt context. The vendor and transaction ref are only known when
// eSign starts (dadPersonal, just before the redirect to the vendor); the
// success / failure events fire on the RETURN page after a full reload. Not
// PII, so it is always kept in sessionStorage (independent of persistContext).
// ---------------------------------------------------------------------------
const ESIGN_KEY = 'ekyc_moe_esign';

/** @param {{ provider?: string, txnId?: string }} attempt */
export const rememberEsign = (attempt = {}) => {
    const value = {
        provider: clean(attempt.provider).toLowerCase() || undefined,
        txnId: clean(attempt.txnId) || undefined,
    };
    if (!value.provider && !value.txnId) return;
    try {
        window.sessionStorage.setItem(ESIGN_KEY, JSON.stringify(value));
    } catch (_) { /* storage blocked — the return page simply won't have it */ }
};

/** @returns {{ provider?: string, txnId?: string }} */
export const getEsignContext = () => {
    try {
        const raw = window.sessionStorage.getItem(ESIGN_KEY);
        return raw ? JSON.parse(raw) || {} : {};
    } catch (_) {
        return {};
    }
};

const safeLeadId = () => {
    try {
        return getLeadId();
    } catch (_) {
        return null;
    }
};

/** Current best-known user values. Missing ones are undefined. */
export const getUserContext = () => {
    const user = readUserState();
    return {
        mobile: remembered.mobile || clean(user.mobileNumber) || undefined,
        email: remembered.email || clean(user.email) || clean(safeVerifiedEmail()) || undefined,
        // Registration name. redux user.fullName is replaced by the PAN name after
        // account creation, so the remembered value (set at OTP verify) wins.
        fullName: remembered.fullName || clean(user.fullName) || undefined,
        // eKYC lead id: user.id (= signup leadId), else sessionStorage leadId.
        ekycUserId: remembered.ekycUserId || clean(user.id) || clean(safeLeadId()) || undefined,
        clientName: remembered.clientName || clean(user.fullName) || undefined,
        clientId: remembered.clientId || clean(user.clientCode) || undefined,
        dob: remembered.dob || undefined,
    };
};
